import { useEffect, type ComponentProps, type ReactNode } from "react";
import { createPortal } from "react-dom";

import { t } from "../contract/strings";
import { cx } from "./cx";
import { CloseIcon } from "./icons";

export interface OverlayProps extends Omit<ComponentProps<"div">, "children" | "onClose"> {
  /** Instrument label for the top-left corner. Optional: a plate whose contents name
   *  themselves needs no second title, and the way out stands alone on the right. */
  label?: ReactNode;
  onClose: () => void;
  /** Hand the body the whole height under the header instead of centring it, scrolling
   *  when its contents are taller, for an overlay that spaces its own contents (Add
   *  credits). Off for every other overlay. */
  fill?: boolean;
  children?: ReactNode;
}

/**
 * One task, full screen.
 *
 * The overlays hold the app's verbs -- name a session, listen for a chirp, enter a code --
 * and each verb gets the whole plate, opaque and ruled like every other page: a labelled
 * header with the way out, then its body centred in what remains. Nothing of the screen it
 * replaced stays visible, because the task owns the screen until it is done.
 *
 * Escape closes, as does the drawn button; both go through the one `onClose`, because
 * closing always has a second half (a chirp listener to stop, an attempted code to
 * forget) and that half belongs to the caller.
 *
 * The entrance is the screen's own slide -- the same `screen-in` keyframes every route
 * uses, so arriving here feels like navigating rather than like something popping over.
 */
export function Overlay({ label, onClose, fill = false, children, className, ...rest }: OverlayProps) {
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);

  // Portalled to the body, and that is load-bearing. Every route renders inside `Screen`,
  // whose entrance animation leaves a filled transform on its `<section>` -- and an
  // ancestor with a filled transform silently becomes the containing block for
  // `position: fixed` descendants. Inside the tree, this overlay's `inset-0` filled the
  // 34rem content column instead of the phone. [V] Found as "the close button is not in
  // the top-right corner": it was -- of a plate one column wide.
  return createPortal(
    <div
      role="dialog"
      aria-modal="true"
      className={cx(
        "fixed inset-0 z-40 flex flex-col ruled-ground",
        "pt-safe-t pb-safe-b animate-screen-in motion-reduce:animate-none",
        className
      )}
      {...rest}
    >
      {/* The header lives in the same reading column as the body -- so the way out lines
          up exactly with the right edge of the contents beneath it. */}
      <div className="w-full max-w-content mx-auto">
        <header className="h-16 px-4 flex items-center justify-between">
          {label ? (
            <span className="font-code text-label font-semibold tracking-label text-accent">
              {label}
            </span>
          ) : (
            <span />
          )}
          <button
            type="button"
            onClick={onClose}
            aria-label={t("common.close")}
            // No box at all -- not even a hover plate. The X itself is the control; it
            // answers with colour, and keeps its full-size hit area via the negative
            // margin. The explicit reset matters: an unstyled button wears the browser's
            // own background and border otherwise.
            className="p-2 -m-2 border-none bg-transparent text-ink-soft hover:text-accent button-transition enabled:active:opacity-70"
          >
            <CloseIcon className="size-icon-md" />
          </button>
        </header>
      </div>
        {/* Centred in the remainder, lifted clear of the home indicator -- and held to
            the same reading column as every page, whatever the viewport's width. */}
        <div className={cx("flex-1 flex flex-col", fill ? "min-h-0 overflow-y-auto" : "justify-center pb-16")}>
          <div className={cx("w-full max-w-content mx-auto px-pad", fill && "flex-1 flex flex-col")}>
            {children}
          </div>
        </div>
      </div>,
      document.body
  );
}
